import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { addToWatchlist, removeFromWatchlist } from '../redux/slices/userSlice';
import { setCurrentlyPlaying } from '../redux/slices/contentSlice';
import { getContentById } from '../data/mockData';
import styled from 'styled-components';

const BannerContainer = styled.div`
  position: relative;
  height: 80vh;
  min-height: 600px;
  display: flex;
  align-items: center;
  padding: 0 4%;
  background: linear-gradient(
    77deg,
    rgba(0, 0, 0, 0.6) 0%,
    transparent 85%
  ),
  linear-gradient(
    166deg,
    rgba(0, 0, 0, 0.4) 0%,
    transparent 40%
  ),
  linear-gradient(
    to bottom,
    transparent 0%,
    rgba(20, 20, 20, 0.8) 100%
  ),
  url(${props => props.$backgroundImage}) center/cover no-repeat;

  @media (max-width: 768px) {
    height: 70vh;
    min-height: 500px;
    padding: 0 20px;
  }
`;

const ContentWrapper = styled(motion.div)`
  max-width: 550px;
  z-index: 2;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Description = styled(motion.p)`
  font-size: 1.4rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  max-width: 480px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 1rem;
  }
`;

const MetadataContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  font-size: 1rem;
  color: #d2d2d2;

  @media (max-width: 768px) {
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }
`;

const Rating = styled.span`
  background: rgba(255, 255, 255, 0.2);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
`;

const Separator = styled.span`
  color: #808080;
`;

const Genre = styled.span`
  color: #d2d2d2;
`;

const IMDBRating = styled.span`
  background: rgba(245, 197, 24, 0.2);
  color: #f5c518;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.3rem;

  &::before {
    content: '⭐';
    font-size: 0.9em;
  }
`;

const ButtonContainer = styled(motion.div)`
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.8rem;
  }
`;

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: black;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 1rem;
    width: 100%;
    justify-content: center;
  }
`;

const MyListButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(109, 109, 110, 0.7);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(109, 109, 110, 0.4);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 1rem;
    width: 100%;
    justify-content: center;
  }
`;

const MoreInfoButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(109, 109, 110, 0.7);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: rgba(109, 109, 110, 0.4);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 0.6rem 1.5rem;
    font-size: 1rem;
    width: 100%;
    justify-content: center;
    text-align: center;
  }
`;

const PlayIcon = styled.span`
  font-size: 1.2rem;
`;

const InfoIcon = styled.span`
  font-size: 1.2rem;
`;

const ListIcon = styled.span`
  font-size: 1.2rem;
`;

const HeroBanner = () => {
  const dispatch = useDispatch();
  const { allContent, scrapedContent, apiHealthy } = useSelector(state => state.content);
  const { currentProfile } = useSelector(state => state.user);

  const [featuredContent, setFeaturedContent] = useState(null);

  useEffect(() => {
    let featured = null;

    // First try to get featured content from scraped data if API is healthy
    if (apiHealthy && scrapedContent.trending.length > 0) {
      featured = scrapedContent.trending.find(item => item.featured) || scrapedContent.trending[0];
    }

    // Fallback to mock data
    if (!featured) {
      featured = allContent.find(item => item.featured) || (allContent.length > 0 ? allContent[0] : null);
    }

    setFeaturedContent(featured);
  }, [allContent, scrapedContent, apiHealthy]);

  const handlePlay = () => {
    if (featuredContent) {
      // Redirect to external watch page instead of local video player
      if (featuredContent.watchseriesUrl) {
        window.open(featuredContent.watchseriesUrl, '_blank');
      } else {
        // Fallback to local player if no external URL available
        dispatch(setCurrentlyPlaying(featuredContent));
      }
    }
  };

  const handleMyListToggle = () => {
    if (!featuredContent || !currentProfile) return;

    const isInWatchlist = currentProfile.watchlist.includes(featuredContent.id);

    if (isInWatchlist) {
      dispatch(removeFromWatchlist(featuredContent.id));
    } else {
      dispatch(addToWatchlist(featuredContent.id));
    }
  };

  if (!featuredContent) {
    return null;
  }

  const isInWatchlist = currentProfile?.watchlist.includes(featuredContent.id);

  return (
    <BannerContainer $backgroundImage={featuredContent.bannerUrl}>
      <ContentWrapper
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <Title
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          {featuredContent.title}
        </Title>

        <MetadataContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        >
          {featuredContent.imdbRating && (
            <>
              <IMDBRating>{featuredContent.imdbRating}</IMDBRating>
              <Separator>•</Separator>
            </>
          )}
          <Rating>{featuredContent.rating}</Rating>
          <Separator>•</Separator>
          <span>{featuredContent.year}</span>
          <Separator>•</Separator>
          <span>{featuredContent.duration}</span>
          <Separator>•</Separator>
          <Genre>{featuredContent.genre.slice(0, 2).join(', ')}</Genre>
        </MetadataContainer>

        <Description
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
        >
          {featuredContent.description}
        </Description>

        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
        >
          <PlayButton
            onClick={handlePlay}
            aria-label={`Play ${featuredContent.title}`}
          >
            <PlayIcon>▶</PlayIcon>
            Play
          </PlayButton>

          <MyListButton
            onClick={handleMyListToggle}
            aria-label={
              isInWatchlist
                ? `Remove ${featuredContent.title} from My List`
                : `Add ${featuredContent.title} to My List`
            }
          >
            <ListIcon>{isInWatchlist ? '✓' : '+'}</ListIcon>
            My List
          </MyListButton>

          <MoreInfoButton
            to={`/title/${featuredContent.id}`}
            aria-label={`More info about ${featuredContent.title}`}
          >
            <InfoIcon>ⓘ</InfoIcon>
            More Info
          </MoreInfoButton>
        </ButtonContainer>
      </ContentWrapper>
    </BannerContainer>
  );
};

export default HeroBanner;